import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";

import { crmPersonFrom } from "components/integrations/sendableContacts";
import { SendOpportunityToCRM } from "components/integrations/SendOpportunityToCRMButton";
import type { CrmPerson } from "components/integrations/SendToParagonCrmModal";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { LoggedEntityType } from "lib/types/graphQLEnums";
import CreateBidButton from "../../components/bids/create_bid_button/CreateBidButton";
import LeadAttachments from "../../components/leads/lead_details/LeadAttachments";
import LeadCommentsTab from "../../components/leads/lead_details/LeadCommentsTab";
import { LeadContactsSection } from "../../components/leads/lead_details/LeadContactsSection";
import LeadDetailsTab from "../../components/leads/lead_details/LeadDetailsTab";
import { LeadReportsSection } from "../../components/leads/lead_details/LeadReportsSection";
import LeadSummary from "../../components/leads/lead_details/LeadSummary";
import { LegacyLeadOverview } from "../../components/leads/lead_details/LegacyLeadOverview";
import { updateLeadSchema } from "../../components/leads/lead_details/useLeadUpdateFormSubmit";
import { getLeadEntitiesForBid } from "../../components/leads/leads_table/utils";
import { ScheduleReminderButton } from "../../components/reminders/ScheduleReminderButton";
import { RouterBreadcrumb } from "../../components/ui/breadcrumb/SimpleBreadcrumb";
import Button from "../../components/ui/button/Button";
import EmptyState from "../../components/ui/empty_state/EmptyState";
import TextAreaInput from "../../components/ui/input/TextAreaInput";
import toast from "../../components/ui/toast/Toast";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { useAutoGenerateLeadSuggestedContacts } from "../../lib/hooks/api/leads/useAutoGenerateLeadSuggestedContacts";
import { useDeleteLeads } from "../../lib/hooks/api/leads/useDeleteLeads";
import { useLead } from "../../lib/hooks/api/leads/useLead";
import { useLeadContactSuggestionsStatus } from "../../lib/hooks/api/leads/useLeadContactSuggestionsStatus";
import { useLeadEntityActions } from "../../lib/hooks/api/leads/useLeadEntityActions";
import { useUpdateLeads } from "../../lib/hooks/api/leads/useUpdateLeads";
import { useUnifiedContacts } from "../../lib/hooks/useUnifiedContacts";
import UIcon from "../../lib/icons/untitled_ui/UIcon";
import { routes } from "../../lib/routes";
import { Flex } from "../../styles/utility-components";

type SidebarContent = "details" | "comments" | "attachments";
export default function LeadDetailsPage({ id }: { id: string }) {
  const { data: lead, isLoading, isError } = useLead(id);
  useLogEntityViewOnOpen(LoggedEntityType.Lead, id);

  /**
   * The people this lead could be sent to the CRM as, where the mapping writes to an object that
   * holds one. Already saved to the lead, so their details are revealed and no credit is spent on
   * reading them — which the suggestions are not, and is why only these are offered.
   */
  const sendableContacts: CrmPerson[] = useMemo(
    () => (lead?.contacts ?? []).map(crmPersonFrom),
    [lead?.contacts],
  );

  const navigate = useNavigate();
  const [sidebarTab, setSidebarTab] = useState<SidebarContent>("details");
  const isUnifiedContactsEnabled = useUnifiedContacts();

  const { isGenerating: isGeneratingSuggestions, markGenerationRequested } =
    useLeadContactSuggestionsStatus(id, isUnifiedContactsEnabled);

  // Leads created before contact recommendations existed have none, and asking for them is the
  // page's job rather than the user's. `contactSuggestionsRequestedAt` is what marks a lead as
  // already asked — the row count can't, because a search that found nobody leaves none behind.
  // It is also what caps the tease at one batch: a company without CONTACTS gets a single
  // generated batch (redacted server-side) and no top-up, because only the entitled card offers
  // "load more".
  //
  // Deliberately not gated on CONTACTS access: an unentitled company needs a real batch for the
  // lead to tease what the feature does. The server withholds every identifying field from them
  // (see `getSuggestedContacts`), so generating one exposes nothing.
  const canRecommendContacts = isUnifiedContactsEnabled;
  const isRequestingSuggestions = useAutoGenerateLeadSuggestedContacts({
    leadId: id,
    enabled:
      canRecommendContacts &&
      !!lead &&
      !lead.contactSuggestionsRequestedAt &&
      (lead.buyers?.length ?? 0) > 0,
    onRequested: markGenerationRequested,
  });

  const {
    control,
    reset,
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useForm({
    resolver: zodResolver(updateLeadSchema.pick({ name: true })),
    defaultValues: { name: lead?.name ?? "" },
  });

  useEffect(() => {
    if (lead) {
      reset({ name: lead.name });
    }
  }, [lead, reset]);

  const { mutate: updateLeads } = useUpdateLeads({
    onSuccess: () => {
      toast.success("Lead title updated successfully");
      reset({}, { keepValues: true });
    },
    onError: () => toast.error("Failed to update lead title"),
  });

  const submitNameUpdate = async () => {
    if (!dirtyFields.name) return;
    const isValid = await trigger("name");
    if (isValid) {
      updateLeads({
        updateLeadsRequest: {
          ids: [id],
          updateData: { name: getValues("name") },
        },
      });
    }
  };

  const { mutate: deleteLeads, isLoading: isDeleting } = useDeleteLeads({
    onSuccess: () => {
      toast.success("Lead deleted successfully");
      void navigate({ to: "/leads" });
    },
    onError: (error) => {
      console.warn("Failed to delete lead", error);
      toast.error("Failed to delete lead");
    },
  });

  const { removeLeadEntities } = useLeadEntityActions({ leadId: id });

  if (isLoading || isDeleting) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center" height="100%">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this lead"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!lead) {
    return (
      <Flex flexGrow={1} alignItems="center" height="100%">
        <EmptyState
          type="loadError"
          heading="Lead not found"
          description="The lead you are looking for does not exist."
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  const sidebarContent = () => {
    switch (sidebarTab) {
      case "details":
        return <LeadDetailsTab lead={lead} />;
      case "comments":
        return <LeadCommentsTab leadId={lead.id} leadName={lead.name} comments={lead.comments} />;
      case "attachments":
        return <LeadAttachments lead={lead} />;
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <HeaderContainer aria-label="Lead details page header">
        <RouterBreadcrumb overrides={{ [`/leads/${id}`]: { label: lead.name } }} />
        <Flex alignItems="center" gap={8}>
          <Button
            buttonStyle="inverse"
            buttonType="primary"
            onClick={() => setSidebarTab("details")}
            className={sidebarTab === "details" ? "active" : undefined}
          >
            Details
          </Button>
          <Button
            buttonStyle="inverse"
            buttonType="primary"
            onClick={() => setSidebarTab("comments")}
            className={sidebarTab === "comments" ? "active" : undefined}
          >
            Comments
          </Button>
          <Button
            buttonStyle="inverse"
            buttonType="primary"
            onClick={() => setSidebarTab("attachments")}
            className={sidebarTab === "attachments" ? "active" : undefined}
          >
            Attachments
          </Button>
        </Flex>
      </HeaderContainer>

      <ContentContainer>
        <MainContent>
          <CenteredContent>
            <HeaderSection>
              <LeadBadge>
                <UIcon icon="target04" /> Lead
              </LeadBadge>
              <TextAreaInput
                fontStyle="h1"
                name="name"
                control={control}
                placeholder="Enter lead title"
                borderless
                required
                onBlurCapture={submitNameUpdate}
              />

              {/* Action buttons */}
              <Flex gap={8} aria-label="Lead details actions">
                {lead.bid ? (
                  <Button
                    as="a"
                    buttonType="secondary"
                    icon="feather"
                    href={`/bids/${lead.bid.id}`}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      const bidId = lead.bid?.id;
                      if (bidId) void navigate({ to: `/bids/${bidId}` });
                    }}
                  >
                    View bid
                  </Button>
                ) : (
                  <CreateBidButton
                    createdFrom="lead"
                    trackingData={{
                      "Context source": "Lead details page",
                    }}
                    onCreateBid={(bidId) => {
                      void navigate({ to: `/bids/${bidId}` });
                    }}
                    defaultValues={{
                      name: lead?.name,
                      // A deactivated assignee can't take the bid, so it goes to its creator
                      assignedToId: lead.assignedTo?.active ? lead.assignedTo.guid : undefined,
                      value: lead.value ?? undefined,
                      entities: getLeadEntitiesForBid(lead),
                      leadId: lead?.id,
                    }}
                    buttonProps={{
                      buttonType: "secondary",
                      iconProps: { icon: "feather" },
                    }}
                  />
                )}

                <SendOpportunityToCRM
                  allowParagonCrms
                  people={sendableContacts}
                  opportunity={{
                    guid: lead.id,
                    name: lead.name,
                    value: lead.value ?? undefined,
                    createDate: lead.createdAt ?? "",
                    closeDate: lead.expiryDate ?? undefined,
                    stotlesRoute: `${routes.leads}/${id}`,
                  }}
                />
                <ScheduleReminderButton
                  dataType="lead"
                  dataId={lead.id}
                  dataTitle={lead.name}
                  anchorDate={lead.expiryDate}
                  anchorType="expiry"
                  buttonRender={
                    <Button buttonType="secondary" icon="clockRewind">
                      Set reminder
                    </Button>
                  }
                />
                <Button
                  buttonType="secondary"
                  icon="trash01"
                  onClick={() => deleteLeads({ ids: [lead.id] })}
                >
                  Delete lead
                </Button>
              </Flex>
            </HeaderSection>
            <LeadSummary lead={lead} />
            {/* The unified contacts experience gates the whole relayout: Reports + Contacts
                sections with the buyer activity report moved to its own route. Off, the lead keeps
                its previous shape — see LegacyLeadOverview, which goes when the gate does. */}
            {isUnifiedContactsEnabled ? (
              <Flex column gap={32}>
                <LeadReportsSection lead={lead} />
                <LeadContactsSection
                  lead={lead}
                  showSuggestions={isUnifiedContactsEnabled}
                  isGeneratingSuggestions={isGeneratingSuggestions || isRequestingSuggestions}
                  removeEntity={(entity) => removeLeadEntities([entity])}
                />
              </Flex>
            ) : (
              <LegacyLeadOverview
                lead={lead}
                removeEntity={(entity) => removeLeadEntities([entity])}
              />
            )}
          </CenteredContent>
        </MainContent>

        {/* Sidebar */}
        <Sidebar>{sidebarContent()}</Sidebar>
      </ContentContainer>
    </PageContainer>
  );
}

const PageContainer = styled.div({
  height: "100vh",
  flex: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

const HeaderContainer = styled(Flex)(({ theme }) => ({
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  justifyContent: "space-between",
  alignItems: "center",
}));

const ContentContainer = styled.div({
  flex: 1,
  display: "flex",
  overflow: "hidden",
});

const MainContent = styled(Flex)({
  overflow: "auto",
  padding: 48,
  flexDirection: "column",
  flexGrow: 1,
});

const CenteredContent = styled(Flex)({
  width: "100%",
  maxWidth: 880,
  margin: "0 auto",
  flexDirection: "column",
  gap: 48,
});

const HeaderSection = styled(Flex)({
  flexDirection: "column",
  gap: 16,
});

const LeadBadge = styled(Flex)(({ theme }) => ({
  gap: 8,
  alignItems: "center",
  padding: "8px 10px",
  background: theme.colors.buildPipelineLight,
  color: theme.colors.buildPipelineDark,
  height: 32,
  width: "fit-content",
  border: `1px solid ${theme.colors.buildPipelineDark}`,
  borderRadius: 4,
}));

const Sidebar = styled(Flex)(({ theme }) => ({
  width: 440,
  flexShrink: 0,
  borderLeft: `1px solid ${theme.colors.sysBorderDecorative}`,
  overflow: "auto",
  background: theme.colors.sysBackgroundDefault,

  "& > *": {
    minWidth: "100%",
  },
}));
